import React, { useCallback, useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";

import CallbackSuccess from "@/components/Organisms/CallbackSuccess";
import FormInputs from "./FormInputs";

import useRegisterForm from "./useRegisterForm";
import useFormHandler from "./useFormHandler";

import { getURLParams } from "@/utils";
import Typography from "@/foundation/Typography/V2";

import { FormInputsI } from "./formMap";

import styles from "./FormInputs.module.scss";

interface RegistrationFormProps extends FormInputsI {
  title?: string;
  urlsArguments: string;
  isShyftPassRenewalPage?: boolean;
  shyftPassZoneList?: { name: string; id: number }[];
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  title,
  urlsArguments,
  defaultPackageSlug,
  packages,
  isShyftPassRenewalPage,
  shyftPassZoneList,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const { methods, mapData, selectedPackage, resetData } = useRegisterForm({
    urlsArguments,
    defaultPackageSlug,
    packages,
    otherData: {
      ...(isShyftPassRenewalPage && {
        is_shyftpass_renewal: isShyftPassRenewalPage,
      }),
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  const setDefaultValuesFromParams = useCallback(() => {
    const params = getURLParams();
    const name = params?.get("name") || "";
    const mobile = params?.get("phone") || "";
    const email = params?.get("email") || "";
    const defaultDuration = Number(params?.get("recommended_package_duration"));

    if (name || mobile || email || defaultDuration) {
      reset((formValues) => ({
        ...formValues,
        name,
        mobile,
        email,
        duration: defaultDuration || 1,
      }));
    }
  }, [reset]);

  useEffect(() => {
    setDefaultValuesFromParams();
  }, [setDefaultValuesFromParams]);

  const { handleBlur, onSubmitCallback } = useFormHandler({
    methods,
    mapData,
    pricing: {
      slug: selectedPackage?.slug,
      price: selectedPackage?.price,
      currency: selectedPackage?.currencyCode,
      package_duration: selectedPackage?.duration,
    },
    setSuccess,
    setIsLoading,
    paymentSuccessPath: "/shyftpass/payments/success",
    paymentFailurePath: "/shyftpass/payments/failure",
  });

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const errors = document.querySelectorAll(".form-input-error");
      // eslint-disable-next-line no-unused-expressions
      errors?.[0]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [errors]);

  const successModalSecondaryCTA = (
    <Typography variant="paragraph4">
      Want to learn more about Shyft?{" "}
      <Typography variant="routelink2" to="/">
        Visit our website
      </Typography>
    </Typography>
  );

  const SuccessModalComp = (
    <div className={styles.successView}>
      <CallbackSuccess
        onButtonClick={() => {
          resetData();
          setSuccess(false);
        }}
        secondaryCTA={successModalSecondaryCTA}
      />
    </div>
  );

  const FormComp = (
    <div className={styles.registerForm} id="buynow">
      <div className={styles.formHeading}>
        <h3 className={styles.title}>{title}</h3>
        <p className={styles.subTitle}>
          Shyftpass is only live in Gurgaon & South Delhi presently. When you
          buy a pass, you can attend a class in any zone.
        </p>
      </div>
      <div className={styles.formInputs}>
        <FormProvider {...methods}>
          <FormInputs
            isLoading={isLoading}
            control={control}
            handleSubmit={handleSubmit}
            handleBlur={handleBlur}
            onSubmitCallback={onSubmitCallback}
            packages={packages}
            selectedPackage={selectedPackage}
            isShyftPassRenewalPage={isShyftPassRenewalPage}
            shyftPassZoneList={shyftPassZoneList}
            {...rest}
          />
        </FormProvider>
      </div>
    </div>
  );

  return success ? SuccessModalComp : FormComp;
};

export default RegistrationForm;
